import { get } from "@klw/fetch";
import { WaveFormDatum } from "@quantumcast/ui/src/atoms/WaveFormAudio/WaveFormAudio";
import { assign, createMachine } from "xstate";
import { z } from "zod";
import { millisecondsToStringDuration, stringDurationToMilliseconds, transformMillisecondsToStringDuration } from "../../../../utils/time";
import { HighlightableCue, RawCue } from "../../types";
import { SAMPLES_TO_WIDTH_FACTOR } from "./const";
import { getWidthForDuration } from "./Player/utils";

const cuePairs = [["CUE_IN", "CUE_OUT"], ["FADE_IN", "FADE_OUT"], ["START_VOICE"], ["START_NEXT"]] as const

const CUE_IN = "CUE_IN";
const CUE_OUT = "CUE_OUT";
const FADE_IN = "FADE_IN";
const FADE_OUT = "FADE_OUT";

const sortArrayByCuePairs = (input) => {
    const mapping = {};

    for (let i = 0; i < cuePairs.length; i++) {
        for (const element of cuePairs[i]) {
            mapping[element] = i;
        }
    }

    const sortedArray = input.sort((a, b) => {
        return mapping[a.type] - mapping[b.type];
    });


    return sortedArray
}

const groupCues = (input) => {
    const data = sortArrayByCuePairs(input)

    return data.reduce((prev, current, index, array) => {
        const groupedCues = { ...prev }
        const parsedValue = millisecondsToStringDuration.safeParse(current.value)
        const formattedDuration = parsedValue.success ? parsedValue.data : '00:00.000'

        if (Object.keys(prev).includes(current.key)) {
            groupedCues[current.key].push({ ...current, isHighlighted: false, value: current.value, stringValue: formattedDuration })
        } else {
            groupedCues[current.key] = [{ ...current, isHighlighted: false, value: current.value, stringValue: formattedDuration }]
        }

        return groupedCues
    }, {})
}


const errorMap: z.ZodErrorMap = (error, ctx) => {
    /*
    This is where you override the various error codes
    */
    switch (error.code) {
        case z.ZodIssueCode.too_small:
            const minimum = transformMillisecondsToStringDuration(error.minimum)

            if (error.inclusive) {
                return {
                    message: `Timestamp must be greater than or equal to ${minimum}`
                }
            }
            return {
                message: `Timestamp must be greater than ${minimum}`
            }


        case z.ZodIssueCode.too_big:
            const maximum = transformMillisecondsToStringDuration(error.maximum)
            if (error.inclusive) {
                return {
                    message: `Timestamp must be less than or equal to ${maximum}`
                }
            }
            return {
                message: `Timestamp must be less than ${maximum}`
            }

        case z.ZodIssueCode.custom:
            // produce a custom message using error.params
            // error.params won't be set unless you passed
            // a `params` arguments into a custom validator
            const params = error.params || {};
            if (params.myField) {
                return { message: `Bad input: ${params.myField}` };
            }
            break;
    }
    // fall back to default message!
    return { message: ctx.defaultError };
};


const loadAudioData = async (context, event) => {
    if (!context.fileId) {
        throw new Error('fileId is required');
    }
    if (!context.width) {
        throw new Error('width is required');
    }

    const { offset, width } = context;
    const searchParams = new URLSearchParams({ samples: Math.floor(width / (SAMPLES_TO_WIDTH_FACTOR)).toString() });

    // If the event type includes "dragZoom" or "loading", append the offset to the search parameters
    if (event.type.includes('dragZoom') || event.type.includes('loading')) {
        searchParams.append('offset', offset);
    }

    const url = `/api/libraries/${context.libraryId}/files/${context.fileId}/waveform?${searchParams.toString()}`;

    // Use `fetch()` to get the data and return it as a promise
    const resp = await get(url);
    if (resp.ok) {
        const { data } = await resp.body;
        return data as WaveFormDatum[];
    }
    return []
}

type CueMachineContext = {
    libraryId: string;
    cues: RawCue[];
    selectedCueId: RawCue;
    groupedCues: Record<string, HighlightableCue[]>
    selectedGroup: string;
    currentTime: number;
    offset: number;
    data: WaveFormDatum[];
    detailData: WaveFormDatum[];
    duration: number;
    fileId?: string;
    fileUrl: string;
    width: number;
    height: number;
    selectedArea: {
        width: number,
        height: number,
        x: number,
        y: number
    },
    counter: number,
    errors?: {}
};

const initialContext: CueMachineContext = {
    libraryId: "",
    cues: [],
    selectedCueId: undefined,
    groupedCues: {},
    currentTime: 0,
    offset: 0,
    selectedGroup: 'default',
    // Set the remaining fields to undefined to make them optional
    data: undefined,
    detailData: undefined,
    duration: undefined,
    fileId: undefined,
    fileUrl: undefined,
    width: 920,
    height: undefined,
    counter: 0,
    selectedArea: {
        width: undefined,
        height: undefined,
        x: 0,
        y: 0
    }
}

const cueMachine =
    /** @xstate-layout N4IgpgJg5mDOIC5QGMCuYCyBDZALAlgHZgDEATnPgF5gDaADALqKgAOA9rPgC77uEsQAD0QBWAOwAaEAE9E4gJwAOAHSLxANgBMAZg0aAjABZ6RhQF9z0tJhwFiJVKwhZuYAMLpYDZkhAcuXn5BEQRxLQUVHR0TYw0zJR0DHWk5BF0jDRVDAwVxCXoI3S1La3RsPCIwFQAbdiwIIigSCH5qogA3dgBrapsK+2q6hqaETvZkVz5CHx9BAJ5pkLF6LIN6dSMlLVE9VMQlZOz6UVOFHXCNC5KrEH67Ktr6iEgSADMJ1G8mec5F4L8oXWWlU4i29D0Oh2RiMBikskQulUGw0+Qkxgk4h0pTu5QexCeDVeACMaqgyHM-AsggJAYhgaDwZDobD4WljAYVHlRLpCkYtForuIcfdKgThi8ICQALbsL5gGpgLAdOg-Kl-GnLBAMtRMq4sgyifbpAX0FSidYmQxKbYgowivFiobPV6y+VgQhuClqtgapZ07WFRlKCH60Qww3GrQGLRGFQmG16VE6XZ6B22J2EyUkSaEZAKym+wL+0ChBTlrm5BTJAWnLYaY25cRm3K7LQbKEGeLpgaPCWvWDK1W+Iv-Wml+kRONdkO5ZIbG1sg6craiVGGlO6GKiHv451EqU2ADSYBk3xH-j9AIngZBKlWBgxJ1ECj5jaUYPv53yMbhmQUNxlBmgxZpAKiyi4NQqMgdSwKQ7CsB6haXsW17CIg1aiPGsb8hCSgaCcCgNgiCD4Vkhw2koCgWlsShmLumb9hA4HsJBKgIR6OawcOvyoeO6EIJh2Ewu2Oj4YRxFpIcWRQucJzxBs6gMSBTEqFQ7DsNKWZNAAImA3BYPgNQAIKoI07A6a4WAtG0KjjL00GOipLrMepmnaYQUB6QZRmmeZlkGWMhBdJMNKzD6KFjlq6xiVEhrJEkFpEecxqGDouoxPQBj4clRjCrcorOQeakaVpbnSgAkhAiotGQWBQAAWqVyHUiWAkxaoiUJfFyUpCRFriNksKJLo4bluWyl9i5jlgAAMvgsDcHZ1WkBAdVQJ4PHqnx0WFFo8ZVkYSVXNEBiNoYWHGKYqJXPyRHYgVTlTcVNjzYty01Q0ECbQACoZ3oXq1aFAqY+1rkRsYQimN2NmY+38kYULaDseiaJN4rTa9C1LfgK05rgWCeVto6agGlpgxoEMmNEa6aI2eiRM2FFbF2iMKPaj3Ac9kozW9ON43B3CbQAalgZLE5FpM3uT5qU+Iv7ZdEjaxlk5btojJg-gY6P7jzWPvbjNUULKKq-f9LVXvxIPRCoWiKCCVx0XRiiNjGcZ2y+GLbI+E2c72GMvegfMfaQTguG4m0WztZOmKCORZco4hO1ojYdmo+HRGJuxUdWOsqIOHRNDZBL2X0T0EgXozjKF0zhYDltaqIqwqOsmzbLsknyO2KjOxEhivgBVyWLchCsXAgiFVUvFRQGCjGgAtFk9DLyvq8r9GecSk009SwJdrxjESerOzC7GknWHVh+MbRjaL47n7e6gRAO9taE8ucta-KnOGgoClGygtyTlCHkRRwhdk3tNCCYsX7A3kJTbI2Uv51l-inEi4RVB0T0HbQ4FxxBgggcVKBUEYKcDADAq2y5Bqfx2Mg5GxpbQHXoK+EwWV8gfgITzIh7FEL8SBhQwMcJbbGCTnlC4N0jD0NjJWJhphl5wlEOwh+jFprlXIVqfIkRqHf35HQkiWdsgKQhPJO2MIOFgXKh5Ly+lDImTMnwAKWA1EBhDFQxBNCf66LSLsM0+FdhYmMMAkEZjXKlRKppKqionHSx0BsHuL4dB5GojsV8ShjRjXvFOGETdExDyUUVPWQdsZRIEnkOMsJyxHVyCdJIxpziRBic7PkSQHzBN5tjEOxSQYKJ7koU4ehHwbjoo2ME+1en6BkYjAUmQ86V08p0xEeDzTU2SBoD8NC54kVWWaRGlMQxJENBovOrRiDzMDOlXqa51j0EoimWpSRzTJE0E3ZsmcOaWCAA */
    createMachine({
        tsTypes: {} as import("./cueMachine.typegen").Typegen0,
        schema: {
            context: {} as CueMachineContext,
            events: {} as {
                type: "resize",
                width: number,
                height: number,
                offset: number
            } | {
                type: "open",
            } | {
                type: "close"
            } | {
                type: "updateCues",
                cues: HighlightableCue[]
            } | {
                type: "dragZoom",
                offset: number
            } | {
                type: "dragCue"
                offsetInMs: number,
                cuePoint: HighlightableCue
            } | {
                type: "addCuePair"
                pair: typeof cuePairs[number]
            } | {
                type: "change"
                cueId: string,
                value: number
            } | {
                type: "setCueValue",
                value: number,
                cueId: string
            } | {
                type: "removeCuePair"
                pair: typeof cuePairs[number]
            } | {
                type: "focus" | "blur" | "mouseleave" | "mouseenter"
                cueId: string
            } | {
                type: "cancel"
            } | {
                type: "save"
            } | {
                type: "cueKeys",
                value: string

            },
            services: {} as {
                'onUpdateCues': {
                    data: void
                },
            }
        },

        context: initialContext,
        initial: "loading",
        on: {
            resize: {
                target: ".loading",
                actions: ['setSize', 'setSelectedArea'],
                cond: 'sizeIsDifferent'
            },
            updateCues: {
                actions: ['updateCues', 'groupCues']
            }
        },
        states: {
            loading: {
                entry: ['groupCues'],
                exit: ['setSelectedArea'],
                invoke: {
                    src: async (context, event) => loadAudioData(context, event),
                    onDone: [
                        {
                            target: "loaded",
                            actions: ["storeData"],
                        },
                    ],
                },
            },

            loaded: {
                states: {
                    modal: {
                        initial: "close",
                        states: {
                            close: {
                                on: {
                                    open: {
                                        target: "open",
                                    },
                                },
                            },
                            open: {
                                on: {
                                    close: {
                                        target: 'close'
                                    }
                                }
                            },
                        },
                    },

                    zoom: {
                        description: "all states concerning the zoomed area",
                        initial: "loadingDetailAudioData",
                        states: {
                            loadingDetailAudioData: {
                                invoke: {
                                    src: async (context, event) => loadAudioData(context, event),
                                    onDone: [
                                        {
                                            target: "zoomIdle",
                                            actions: "storeDetailData",
                                        },
                                    ],
                                },
                            },
                            zoomIdle: {
                                on: {
                                    dragZoom: {
                                        target: 'loadingDetailAudioData',
                                        actions: ['setOffset'],
                                        cond: 'isOffsetDifferent'
                                    },
                                },
                            },
                        },
                    },

                    cueList: {
                        initial: "idle",
                        entry: ["validateCues"],
                        states: {
                            idle: {
                                on: {
                                    dragCue: {
                                        actions: ['changeDraggedCue', 'validateCues']
                                    },

                                    addCuePair: {
                                        actions: ['addCuePair', 'validateCues']
                                    },
                                    change: {
                                        actions: ['changeCue', 'validateCues']
                                    },
                                    setCueValue: {
                                        actions: ['setCueValue', 'validateCues']
                                    },
                                    removeCuePair: {
                                        actions: ['removeCuePair', 'validateCues']
                                    },
                                },
                            },
                        },
                    }
                },
                on: {
                    focus: {
                        actions: ['setSelectedCueId']
                    },
                    blur: {
                        actions: ['setSelectedCueId']
                    },
                    mouseleave: {
                        actions: ['setSelectedCueId']
                    },
                    mouseenter: {
                        actions: ['setSelectedCueId']
                    },
                    cancel: {
                        description: 'cancel will reset any changes',
                        actions: ['groupCues', 'clearErrors']
                    },
                    save: {
                        target: "saving",
                        cond: "isValidCues",
                    },
                    // TODO: this is a very confusing name
                    cueKeys: {
                        internal: true,
                        actions: ["setSelectedGroup"]
                    }
                },
                type: "parallel",
            },

            saving: {
                invoke: {
                    src: 'onUpdateCues',
                    onDone: [
                        {
                            target: "loaded.modal.close",
                        },
                    ],
                },
            },

            done: {
                type: 'final'
            }
        },
        id: "cueMachine",
    }, {
        guards: {
            isOffsetDifferent: (context, event) => event.offset !== context.offset,
            sizeIsDifferent: (context, event) => event.width !== context.width,
            isValidCues: (context, event) => !Boolean(context.errors)
        },
        actions: {
            updateCues: assign((context, event) => {
                return { cues: event.cues }
            }),
            addCuePair: assign((context, event) => {
                const cues = context.groupedCues[context.selectedGroup]

                const cueIn = cues.find(cue => cue.type === CUE_IN)
                const cueOut = cues.find(cue => cue.type === CUE_OUT)

                const defaultValues = {
                    FADE_IN: cueIn.value + 1000,
                    FADE_OUT: cueOut.value - 1000,
                    START_VOICE: cueIn.value + 2000,
                    START_NEXT: cueOut.value - 2000
                }

                const newCuePair: HighlightableCue[] = [{
                    type: event.pair[0],
                    value: defaultValues[event.pair[0]],
                    stringValue: transformMillisecondsToStringDuration(defaultValues[event.pair[0]]),
                    id: `${event.pair[0]}_${context.selectedGroup}`,
                    isHighlighted: false,
                    key: context.selectedGroup
                }]



                if (event.pair[0] === FADE_IN) {
                    newCuePair.push({
                        type: event.pair[1],
                        value: defaultValues[event.pair[1]],
                        stringValue: transformMillisecondsToStringDuration(defaultValues[event.pair[1]]),
                        id: `${event.pair[1]}_${context.selectedGroup}`,
                        isHighlighted: false,
                        key: context.selectedGroup
                    })
                }

                return {
                    groupedCues: {
                        ...context.groupedCues,
                        [context.selectedGroup]: [
                            ...context.groupedCues[context.selectedGroup],
                            ...newCuePair,
                        ]
                    }
                }
            }),
            removeCuePair: assign((context, event) => {
                return {
                    groupedCues: {
                        ...context.groupedCues,
                        [context.selectedGroup]: context.groupedCues[context.selectedGroup].map(cue => {
                            // @ts-expect-error
                            if (event.pair.includes(cue.type)) {
                                return undefined

                            } else {
                                return cue
                            }
                        }).filter(Boolean)
                    }
                }
            }),
            changeDraggedCue: assign((context, event) => {
                return {
                    groupedCues: {
                        ...context.groupedCues,
                        [context.selectedGroup]: context.groupedCues[context.selectedGroup].map(cue => {
                            if (cue.id === event.cuePoint.id) {
                                const value = cue.value + event.offsetInMs
                                return {
                                    ...cue,
                                    value,
                                    stringValue: transformMillisecondsToStringDuration(value)
                                }
                            } else {
                                return cue
                            }
                        })
                    }
                }
            }),
            setCueValue: assign((context, event) => {
                const stringValue = transformMillisecondsToStringDuration(event.value)

                return {
                    groupedCues: {
                        ...context.groupedCues,
                        [context.selectedGroup]: context.groupedCues[context.selectedGroup].map(cue => {
                            if (cue.id === event.cueId) {
                                return {
                                    ...cue,
                                    value: event.value,
                                    stringValue
                                }
                            } else {
                                return cue
                            }
                        })
                    }
                }
            }),
            // @ts-expect-error
            changeCue: assign((context, event) => {
                const parsedValue = stringDurationToMilliseconds.safeParse(event.value)
                const valueInMs = parsedValue.success ? parsedValue.data : 0
                if (parsedValue.success === false) {
                    // TODO: these errors needs to be written into global error context
                }

                return {
                    groupedCues: {
                        ...context.groupedCues,
                        [context.selectedGroup]: context.groupedCues[context.selectedGroup].map(cue => {
                            if (cue.id === event.cueId) {
                                return {
                                    ...cue,
                                    value: valueInMs,
                                    stringValue: event.value
                                }
                            } else {
                                return cue
                            }
                        })
                    }
                }

            }),
            clearErrors: assign((context, event) => ({
                errors: undefined
            })),
            setSelectedCueId: assign((context, event) => ({
                groupedCues: {
                    ...context.groupedCues,
                    [context.selectedGroup]: context.groupedCues[context.selectedGroup].map(cue => {
                        if (cue.id === event.cueId) {
                            return {
                                ...cue,
                                isHighlighted: true
                            }
                        } else {
                            return {
                                ...cue,
                                isHighlighted: false
                            }
                        }
                    })
                }
            })),

            validateCues: assign((context, event) => {
                const cues = context.groupedCues[context.selectedGroup] || []
                const cueIn = cues.find(cue => cue.type === CUE_IN)
                const cueOut = cues.find(cue => cue.type === CUE_OUT)
                const fadeIn = cues.find(cue => cue.type === FADE_IN)
                const fadeOut = cues.find(cue => cue.type === FADE_OUT)

                const duration = context.duration

                const cueValueSchema = z.array(z.number().gte(0).lte(duration))

                const cueValueResult = cueValueSchema.safeParse([cueIn?.value, cueOut?.value], { errorMap })

                if (cueValueResult.success === false) {
                    console.error(cueValueResult.error.flatten());
                    return { errors: cueValueResult.error.flatten() }
                }

                const hasDuplicatesKeys = (elements: Partial<RawCue>[]): boolean => {
                    const keys = elements.map(e => `${e.type}-${e.key}`);

                    return keys.length !== new Set(keys).size;
                }


                /**
                 * validate any of the custom CUE_TYPES
                 */

                const cuesSchema = z.discriminatedUnion('type', [
                    z.object({
                        id: z.string(),
                        type: z.literal('CUE_IN'),
                        key: z.string(),
                        value: z.number().gte(0).lte(duration),
                    }),
                    z.object({
                        id: z.string(),
                        type: z.literal('CUE_OUT'),
                        key: z.string(),
                        value: z.number().lte(duration).gt(cueIn.value),
                    }),
                    z.object({
                        id: z.string(),
                        type: z.literal('FADE_IN'),
                        key: z.string(),
                        value: z.number().gte(cueIn.value).lte(cueOut.value).lt(fadeOut?.value),
                    }),
                    z.object({
                        id: z.string(),
                        type: z.literal('FADE_OUT'),
                        key: z.string(),
                        value: z.number().gte(cueIn.value).lte(cueOut.value).gt(fadeIn?.value),
                    }),
                    z.object({
                        id: z.string(),
                        type: z.literal('START_VOICE'),
                        key: z.string(),
                        value: z.number().gte(cueIn.value).lte(cueOut.value),
                    }),
                    z.object({
                        id: z.string(),
                        type: z.literal('START_NEXT'),
                        key: z.string(),
                        value: z.number().gte(cueIn.value).lte(cueOut.value),
                    }),
                ]).array()
                    .superRefine((elements, ctx) => {
                        if (hasDuplicatesKeys(elements)) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: 'No duplicated keys allowed.',
                            });
                        }
                    })

                // TODO: ~~validate type of cue, each type should only appear once~~
                // TODO: display the error message generated from validating the whole array on the page, currently the error (formError) is written into the error object but not show, but blocks saving nonehteless

                const result = cuesSchema.safeParse(cues, { errorMap })

                if (result.success === false) {
                    return { errors: result.error.flatten() }
                }
                return { errors: undefined }
            }),
            setSelectedGroup: assign((context, event) => ({
                selectedGroup: event.value
            })),
            groupCues: assign((context, _event) => ({
                groupedCues: groupCues(context.cues)
            })),
            // @ts-expect-error
            setSelectedArea: assign((context, _event) => ({
                selectedArea: {
                    width: getWidthForDuration({
                        availableDuration: context.duration,
                        availableWidth: context.width,
                    }),

                    height: context.height
                }
            })),

            setOffset: assign((context, event) => ({ offset: event.offset })),
            setSize: assign((context, event) => ({ width: event.width, height: event.height })),
            // @ts-expect-error
            storeDetailData: assign((context, event) => ({ detailData: event.data })),
            // @ts-expect-error
            storeData: assign((context, event) => ({ data: event.data }))
        }
    })

const selectors = {
    selectId: state => state.context.fileId,
    selectArea: state => state.context.selectedArea,
    selectCanSave: (state) => state.can('save'),
    selectCues: (state) => state.context.groupedCues[state.context.selectedGroup],
    selectCurrentTime: (state) => state.context.currentTime,
    selectData: (state) => state.context.data,
    selectDetailData: (state) => state.context.detailData,
    selectDuration: (state) => state.context.duration,
    selectErrors: (state) => state.context.errors,
    selectFileUrl: state => state.context.fileUrl,
    selectGroupedCues: (state) => state.context.groupedCues,
    selectSelectedGroup: (state) => state.context.selectedGroup,

    compareCues: (prevCues, nextCues) => {
        return Boolean(prevCues.filter(cue => cue.isHighlighted)[0]?.id === nextCues.filter(cue => cue.isHighlighted)[0]?.id)
    }
}

export { cueMachine, selectors, cuePairs };
